import { useEffect } from 'react';
import type { ReactNode } from 'react';

/**
 * Bottom sheet (Component - Sheet and Form.dc.html). Module-local copy (from `trackers`), same as `apps`,
 * `lists`, `games` and `household` each keep — modules don't import each other's
 * components, and there's no shared component library to put it in.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  tall,
  testId = 'sheet',
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  tall?: boolean;
  testId?: string;
}): JSX.Element | null {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex flex-col justify-end" data-testid={testId}>
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0"
        style={{ background: 'color-mix(in srgb, var(--app-fg) 32%, transparent)' }}
        onClick={onClose}
        data-testid={`${testId}-scrim`}
      />
      <div
        className={`relative flex flex-col gap-3 rounded-t-lg border-t border-line bg-surface px-4 pb-[max(16px,env(safe-area-inset-bottom))] pt-3 shadow-2xl transition-transform duration-200 ease-app ${
          tall
            ? 'max-h-[calc(90vh-env(safe-area-inset-top))]'
            : 'max-h-[calc(70vh-env(safe-area-inset-top))]'
        }`}
      >
        <span className="mx-auto h-1 w-10 rounded-full bg-line" />
        <h4 className="text-lg font-bold text-fg">{title}</h4>
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
